import { api } from '@/app/api'
import type { Paginated, ResourceResponse, ListParams } from '@/lib/types'
import type {
  CustomerPortalSession,
  PortalMeResponse, PortalTicket, PortalAsset, PortalKbResult,
} from './customer-portal-types'

export const customerPortalApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Admin: session management ──────────────────────────
    listPortalSessions: build.query<Paginated<CustomerPortalSession>, ListParams & { customer_id?: number; include_revoked?: boolean }>({
      query: (params) => ({ url: '/v1/customer-portal/sessions', params }),
      providesTags: ['Customer'],
    }),
    createPortalSession: build.mutation<
      ResourceResponse<CustomerPortalSession & { token: string }>,
      { customer_id: number; label?: string; expiry_days?: number }
    >({
      query: (body) => ({
        url: '/v1/customer-portal/sessions?include_token=true',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Customer'],
    }),
    revokePortalSession: build.mutation<unknown, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/v1/customer-portal/sessions/${id}/revoke`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: ['Customer'],
    }),

    // ── Public portal: me ──────────────────────────────────
    portalMe: build.query<{ data: PortalMeResponse }, string>({
      query: (token) => `/portal/${token}/me`,
    }),

    // ── Public portal: tickets ─────────────────────────────
    portalTickets: build.query<
      Paginated<PortalTicket>,
      { token: string; page?: number; per_page?: number; status?: string; asset_id?: string }
    >({
      query: ({ token, ...params }) => ({ url: `/portal/${token}/tickets`, params }),
    }),
    portalTicket: build.query<ResourceResponse<PortalTicket>, { token: string; ticketId: string }>({
      query: ({ token, ticketId }) => `/portal/${token}/tickets/${ticketId}`,
    }),

    // ── Public portal: assets ──────────────────────────────
    portalAssets: build.query<
      Paginated<PortalAsset>,
      { token: string; page?: number; per_page?: number }
    >({
      query: ({ token, ...params }) => ({ url: `/portal/${token}/assets`, params }),
    }),
    portalAsset: build.query<ResourceResponse<PortalAsset>, { token: string; assetId: string }>({
      query: ({ token, assetId }) => `/portal/${token}/assets/${assetId}`,
    }),

    // ── Public portal: KB search ───────────────────────────
    portalKbSearch: build.query<{ data: PortalKbResult[] }, { token: string; q: string }>({
      query: ({ token, q }) => ({ url: `/portal/${token}/kb/search`, params: { q } }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useListPortalSessionsQuery,
  useCreatePortalSessionMutation,
  useRevokePortalSessionMutation,
  usePortalMeQuery,
  usePortalTicketsQuery,
  usePortalTicketQuery,
  usePortalAssetsQuery,
  usePortalAssetQuery,
  usePortalKbSearchQuery,
} = customerPortalApi
